import React, { useMemo, useState } from "react";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import moment from "moment";
import { FaCheckDouble, FaFileMedical, FaUser } from "react-icons/fa";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import CustomButton from "../../../shared/CustomButton";
import TextInput from "../../../shared/TextInput";
import { fetchData } from "../../../helpers/fetchData";
import { CalculateAge } from "../../../utils/CalculateAge";
import { extractTextFromHTML } from "../../../utils/ExtractTextFromHtml";
import CheckPermission from "../../../hooks/CheckPermission";

const getVitalValue = (emrData, category) => {
  const vitalSigns = Array.isArray(emrData?.vital_signs)
    ? emrData.vital_signs.flat()
    : [];
  return vitalSigns.find(v => v?.category === category)?.value || "";
};

const getBloodPressure = emrData => {
  const systolic = getVitalValue(emrData, "vital-sign-systolic");
  const diastolic = getVitalValue(emrData, "vital-sign-diastolic");
  return systolic || diastolic ? `${systolic}/${diastolic}` : "";
};

const latestText = records => {
  if (!Array.isArray(records)) return "";
  const last = records[records.length - 1];
  return extractTextFromHTML(last?.notes || last?.value || last || "");
};

const getDiagnosisText = emrData =>
  (emrData?.diagnosisArr || [])
    .map(d => d?.codeDescription || d?.diagnosis_text || d?.name)
    .filter(Boolean)
    .join(", ");

const dispositionOptions = [
  { label: "Discharge", value: "Discharge" },
  { label: "Clinic Follow-UP", value: "Clinic Follow-UP" },
  { label: "Hospital Admission", value: "Hospital Admission" },
];

const toInfoLookup = source => {
  const id = source?.id || source?._id;
  return {
    ...(id ? { id } : {}),
    name_en: source?.name_en || "",
    name_ar: source?.name_ar || "",
  };
};

const toPractitionerInfo = practitioner => ({
  practitioner_id: practitioner?.practitioner_id || practitioner?.id || "",
  rcm_ref: practitioner?.rcm_ref || 0,
  name: practitioner?.name || practitioner?.full_name || "",
  name_ar: practitioner?.name_ar || "",
  document_id: practitioner?.document_id || "",
  room_no: practitioner?.room_no || "",
  ward_no: practitioner?.ward_no || "",
  license: practitioner?.license || practitioner?.license_id || "",
  stamp: practitioner?.stamp || "",
  signature: practitioner?.signature || "",
  profile_photo: practitioner?.profile_photo || practitioner?.profile_image || "",
  department: toInfoLookup(practitioner?.department),
  speciality: toInfoLookup(practitioner?.speciality),
  extra_attachments: practitioner?.extra_attachments || [],
  session_types: practitioner?.session_types || [],
});

const CreateReferralForm = ({
  emrData,
  patientData,
  referralData,
  isEdit,
  setOpenReferralFormModal,
  setSelectEmrData,
  fetchInitialData,
}) => {
  const { hasPermission } = CheckPermission();
  const { t } = useTranslation();
  const { roleData } = useSelector(state => state?.auth);
  const [isLoading, setIsLoading] = useState(false);

  const patient = useMemo(
    () => referralData?.patient || patientData || emrData?.patient || {},
    [emrData?.patient, patientData, referralData?.patient]
  );
  const practitioner = useMemo(
    () => referralData?.practitioner || emrData?.practitioner || roleData || {},
    [emrData?.practitioner, referralData?.practitioner, roleData]
  );
  const practitionerInfo = useMemo(
    () => toPractitionerInfo(practitioner),
    [practitioner]
  );

  const initialValues = useMemo(() => ({
    visit_date: referralData?.visit_date || moment(emrData?.created_at || new Date()).format("YYYY-MM-DD"),
    to_hospital: referralData?.to_hospital || "",
    department: referralData?.department || emrData?.practitioner?.department?.name_en || "",
    age: referralData?.age || CalculateAge(patient?.dob) || "",
    address: referralData?.address || patient?.address || patient?.city || "",
    complaint_and_duration: referralData?.complaint_and_duration || latestText(emrData?.medical_assessments?.chief_complaints),
    physical_examination: referralData?.physical_examination || latestText(emrData?.medical_assessments?.significient_signs),
    blood_pressure: referralData?.vitals?.blood_pressure || getBloodPressure(emrData),
    temperature: referralData?.vitals?.temperature || getVitalValue(emrData, "vital-sign-temperature"),
    respiratory_rate: referralData?.vitals?.respiratory_rate || getVitalValue(emrData, "vital-sign-respiratory-rate"),
    pulse_rate: referralData?.vitals?.pulse_rate || getVitalValue(emrData, "vital-sign-pulse"),
    investigations: referralData?.investigations || "",
    provisional_diagnosis: referralData?.provisional_diagnosis || getDiagnosisText(emrData),
    treatment_given: referralData?.treatment_given || "",
    type_of_referral: referralData?.type_of_referral || "Elective",
    reasons_for_referral: referralData?.reasons_for_referral || "",
    treating_doctor_name: referralData?.treating_doctor_name || practitionerInfo?.name || "",
    treating_doctor_id: referralData?.treating_doctor_id || practitionerInfo?.practitioner_id || "",
    hospital_file_number: referralData?.hospital_report?.hospital_file_number || "",
    findings_and_diagnosis: referralData?.hospital_report?.findings_and_diagnosis || getDiagnosisText(emrData),
    treatment_and_recommendations: referralData?.hospital_report?.treatment_and_recommendations || "",
    disposition: referralData?.hospital_report?.disposition || "",
  }), [emrData, patient, practitionerInfo, referralData]);

  const validationSchema = Yup.object().shape({
    visit_date: Yup.string().required(t("Date is required")),
    to_hospital: Yup.string().trim().required(t("Hospital is required")),
    department: Yup.string().trim().required(t("Department is required")),
    complaint_and_duration: Yup.string().trim().required(t("Complaint and duration is required")),
  });

  const handleSubmit = async values => {
    if (!hasPermission("referral_form", isEdit ? "update" : "create")) return;
    const payload = {
      id: referralData?.id,
      form_type: "internal",
      patient,
      practitioner: practitionerInfo,
      emr_id: emrData?.emr_id || referralData?.emr_id,
      visit_id: String(emrData?.visit_id || referralData?.visit_id || ""),
      visit_date: values.visit_date,
      to_hospital: values.to_hospital,
      department: values.department,
      age: values.age,
      address: values.address,
      complaint_and_duration: values.complaint_and_duration,
      physical_examination: values.physical_examination,
      vitals: {
        blood_pressure: values.blood_pressure,
        temperature: values.temperature,
        respiratory_rate: values.respiratory_rate,
        pulse_rate: values.pulse_rate,
      },
      investigations: values.investigations,
      provisional_diagnosis: values.provisional_diagnosis,
      treatment_given: values.treatment_given,
      type_of_referral: values.type_of_referral,
      reasons_for_referral: values.reasons_for_referral,
      treating_doctor_name: values.treating_doctor_name,
      treating_doctor_id: values.treating_doctor_id,
      hospital_report: {
        hospital_file_number: values.hospital_file_number,
        findings_and_diagnosis: values.findings_and_diagnosis,
        treatment_and_recommendations: values.treatment_and_recommendations,
        disposition: values.disposition,
      },
      status: referralData?.status || "Issued",
    };

    setIsLoading(true);
    const response = await fetchData("POST", isEdit ? "/v3/referral-form/update" : "/v3/referral-form/create", payload);
    setIsLoading(false);
    if (response) {
      toast.success(response?.message);
      setOpenReferralFormModal(false);
      setSelectEmrData?.(null);
      fetchInitialData?.();
    }
  };

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 border p-3 rounded-lg bg-gray-100 mt-2">
        <div className="flex items-center gap-2"><FaUser /><b>{t("Patient Name")}:</b><span>{patient?.full_name || "--"}</span></div>
        <div className="flex items-center gap-2"><FaFileMedical /><b>{t("MRN")}:</b><span>{patient?.file_no || patient?.mrn || "--"}</span></div>
        <div className="flex items-center gap-2"><b>{t("Age")}:</b><span>{CalculateAge(patient?.dob) || "--"}</span></div>
      </div>
      <Formik enableReinitialize initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
        {props => (
          <Form>
            <div className="grid grid-cols-2 gap-x-2 gap-y-1 mt-4">
              <TextInput name="visit_date" type="date" label={t("Date*")} placeholder={t("Select referral date")} />
              <TextInput name="to_hospital" label={t("To Hospital*")} placeholder={t("Enter receiving hospital")} />
              <TextInput name="department" label={t("Department*")} placeholder={t("Enter department")} />
              <TextInput name="address" label={t("Address")} placeholder={t("Enter patient address")} />
              <div className="col-span-2 grid grid-cols-1 md:grid-cols-4 gap-x-2">
                <TextInput name="blood_pressure" label={t("BP (mmHg)")} placeholder={t("120/80")} />
                <TextInput name="temperature" label={t("Temp")} placeholder={t("36.5")} />
                <TextInput name="respiratory_rate" label={t("Resp/R")} placeholder={t("18")} />
                <TextInput name="pulse_rate" label={t("Pulse/R")} placeholder={t("72")} />
              </div>
              <TextInput name="type_of_referral" label={t("Type of Referral")} placeholder={t("Elective")} />
              <TextInput name="treating_doctor_name" label={t("Treating Doctor")} placeholder={t("Treating doctor name")} readOnly />
              {[
                ["complaint_and_duration", "Complaint and Duration*", "Enter complaint and duration"],
                ["physical_examination", "Physical Examination", "Enter physical examination notes"],
                ["investigations", "Investigations", "Enter investigations"],
                ["provisional_diagnosis", "Provisional Diagnosis", "Enter provisional diagnosis"],
                ["treatment_given", "Treatment Given", "Enter treatment given"],
                ["reasons_for_referral", "Reasons for Referral", "Enter reason for referral"],
              ].map(([name, label, placeholder]) => (
                <div className="flex flex-col gap-1 col-span-2" key={name}>
                  <label className="input-label">{t(label)}</label>
                  <Field as="textarea" name={name} className="textarea-field" placeholder={t(placeholder)} />
                  {props?.errors?.[name] && props?.touched?.[name] && <div className="text-red-500 text-sm">{props.errors[name]}</div>}
                </div>
              ))}
              <div className="col-span-2 mt-2 border-t pt-3 font-semibold">{t("Hospital Report")}</div>
              <TextInput name="hospital_file_number" label={t("Hospital File No")} placeholder={t("Enter hospital file number")} />
              <div className="flex flex-col gap-1">
                <label className="input-label">{t("Disposition")}</label>
                <div className="flex flex-wrap gap-4 rounded-md border border-gray-200 px-3 py-2 min-h-[38px]">
                  {dispositionOptions.map(option => (
                    <label key={option.value} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-primary"
                        checked={props.values.disposition === option.value}
                        onChange={event => props.setFieldValue("disposition", event.target.checked ? option.value : "")}
                      />
                      <span>{t(option.label)}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex flex-col gap-1 col-span-2">
                <label className="input-label">{t("Findings and Diagnosis")}</label>
                <Field as="textarea" name="findings_and_diagnosis" className="textarea-field" placeholder={t("Enter findings and diagnosis")} />
              </div>
              <div className="flex flex-col gap-1 col-span-2">
                <label className="input-label">{t("Treatment and Recommendations")}</label>
                <Field as="textarea" name="treatment_and_recommendations" className="textarea-field" placeholder={t("Enter treatment and recommendations")} />
              </div>
            </div>
            <div className="flex items-center justify-end mt-3">
              {hasPermission("referral_form", isEdit ? "update" : "create") && <CustomButton loading={isLoading} type="submit" icon={<FaCheckDouble size={12} className="me-2" />} label={isEdit ? t("Update Referral Form") : t("Create Referral Form")} buttonType="primary" />}
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default CreateReferralForm;
